import { ReactNode } from 'react';
import { PermissionSection, PermissionValues } from './types';
import usePermissionsEditor from './usePermissionsEditor';
import usePermissionFieldRenderer from './Permission/usePermissionFieldRenderer';
import { PermissionsContext } from './PermissionsContext';
import PermissionsSections from './PermissionsSections';
import PermissionsSearch from './PermissionsSearch';
import PermissionsEnableAllButton from './PermissionsEnableAllButton';
import PermissionsSaveButton from './PermissionsSaveButton';

// The account-permissions editor root ([2] ID-13343). It owns the engine and renders no layout: the host
// places the parts (`Permissions.Sections`, `.Search`, `.EnableAllButton`, `.SaveButton`) wherever its
// screen needs them, and keeps fetching, adapting and saving itself.

type PermissionsProps = {
  /** Normalized sections, adapted and label-baked by the host. */
  sections: PermissionSection[];
  /** Prefix shown on currency amounts (e.g. 'CAD'). */
  currency: string;
  /** Disables every control and hides Enable All / Save. */
  readOnly?: boolean;
  /** The form's ID, which `Permissions.SaveButton` submits through. Override when two editors share a page. */
  formID?: string;
  /** Receives the values of the dependency-visible items only. */
  onSave: (values: PermissionValues) => void;
  children: ReactNode;
};

export default function Permissions({
  sections,
  currency,
  readOnly = false,
  formID = 'permissionsForm',
  onSave,
  children,
}: PermissionsProps) {
  const renderField = usePermissionFieldRenderer(currency, readOnly);
  const editor = usePermissionsEditor({ sections, onSave, renderField });

  return <PermissionsContext.Provider value={{ editor, readOnly, formID }}>{children}</PermissionsContext.Provider>;
}

Permissions.Sections = PermissionsSections;
Permissions.Search = PermissionsSearch;
Permissions.EnableAllButton = PermissionsEnableAllButton;
Permissions.SaveButton = PermissionsSaveButton;
